/**
 * <s-gallery-pager> — product gallery controller.
 *
 * Mobile pager: keeps the dots, the "1/9" counter and the thumbnail strip in sync with the swiped slide, and
 * scrolls to a slide when its thumbnail is tapped.
 *
 * Variant media: when a variant option changes inside an <s-variant-picker>, the gallery
 * - shows only images whose alt text names one of the selected option values (for example "Green"), plus every
 *   image whose alt text names no option value (shared images such as dimension drawings). If no image is tagged,
 *   every image stays visible.
 * - moves the selected variant's own image to the lead spot.
 * Hidden slides move to the end with the `hidden` attribute, so the desktop lead (`:first-child`) stays the first
 * visible image. Dots and thumbnails follow the same order.
 *
 * Markup contract (inside the element):
 * - [data-product-gallery]   the horizontal scroll track; each child is one slide with data-media-id / data-media-tag
 * - [data-gallery-dot]       optional dots, one per slide, with data-media-id (data-active marks the current one)
 * - [data-gallery-current]   optional element whose text becomes the current slide number
 * - [data-gallery-total]     optional element whose text becomes the visible slide count
 * - [data-gallery-thumbs]    optional thumbnail strip; each <li data-media-id> holds a [data-gallery-thumb] button
 * - script[data-variant-media] optional JSON: { selected, values: [...], variants: { id: { media, values } } }
 */
class SGalleryPager extends HTMLElement {
  #ac;
  #frame = 0;
  #index = -1;
  #data = null;
  #order = [];
  #current = '';

  connectedCallback() {
    this.track = this.querySelector('[data-product-gallery]');
    if (!this.track) return;
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.strip = this.querySelector('[data-gallery-thumbs]');
    this.dotsWrap = this.querySelector('[data-gallery-dot]')?.parentElement || null;
    this.counter = this.querySelector('[data-gallery-current]');
    this.total = this.querySelector('[data-gallery-total]');
    this.#order = [...this.track.children].map((slide) => slide.dataset.mediaId).filter(Boolean);

    this.track.addEventListener('scroll', () => this.#schedule(), { passive: true, signal });
    this.strip?.addEventListener(
      'click',
      (event) => {
        const thumb = event.target.closest('[data-gallery-thumb]');
        if (!thumb) return;
        this.go(this.#thumbs.indexOf(thumb));
      },
      { signal }
    );

    this.#data = this.#readData();
    if (this.#data) {
      document.addEventListener('change', (event) => this.#onOptionChange(event), { signal });
      this.#current = String(this.#data.selected || '');
      this.#applyVariant(this.#current, false);
    }
    this.#update(true);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  get #reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get #slides() {
    return [...this.track.children].filter((slide) => !slide.hidden);
  }

  get #dots() {
    return [...this.querySelectorAll('[data-gallery-dot]')].filter((dot) => !dot.hidden);
  }

  get #thumbs() {
    return [...this.querySelectorAll('[data-gallery-thumb]')].filter((thumb) => !thumb.closest('li')?.hidden);
  }

  #readData() {
    const script = this.querySelector('script[data-variant-media]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('[s-gallery-pager] Invalid variant media data:', error);
      return null;
    }
  }

  #onOptionChange(event) {
    const input = event.target;
    if (!(input instanceof Element) || !input.closest('s-variant-picker')) return;
    const variantId = input.dataset?.variantId || input.selectedOptions?.[0]?.dataset?.variantId;
    if (!variantId || String(variantId) === this.#current) return;
    this.#current = String(variantId);
    this.#applyVariant(this.#current, true);
  }

  #applyVariant(variantId, scroll) {
    const variant = this.#data?.variants?.[variantId];
    if (!variant) return;
    const allValues = new Set(this.#data.values || []);
    const selectedValues = new Set(variant.values || []);
    const slides = new Map([...this.track.children].map((slide) => [slide.dataset.mediaId, slide]));
    const tagOf = (id) => slides.get(id)?.dataset.mediaTag || '';
    const tagged = this.#order.some((id) => allValues.has(tagOf(id)));

    let visible = this.#order.filter((id) => {
      if (!tagged) return true;
      const tag = tagOf(id);
      return !allValues.has(tag) || selectedValues.has(tag);
    });
    if (!visible.length) visible = [...this.#order];

    const lead = String(variant.media || '');
    if (lead && visible.includes(lead)) visible = [lead, ...visible.filter((id) => id !== lead)];
    const hidden = this.#order.filter((id) => !visible.includes(id));

    const currentOrder = [...this.track.children].map((slide) => (slide.hidden ? `-${slide.dataset.mediaId}` : slide.dataset.mediaId));
    const nextOrder = [...visible, ...hidden.map((id) => `-${id}`)];
    if (currentOrder.join() === nextOrder.join()) {
      if (scroll) this.#reset();
      return;
    }

    this.#arrange(this.track, ':scope > [data-media-id]', visible, hidden);
    if (this.dotsWrap) this.#arrange(this.dotsWrap, ':scope > [data-media-id]', visible, hidden);
    if (this.strip) this.#arrange(this.strip, ':scope > [data-media-id]', visible, hidden);
    if (this.total) this.total.textContent = String(visible.length);
    this.#index = -1;
    if (scroll) this.#reset();
    this.#update(true);
  }

  #arrange(container, selector, visible, hidden) {
    const items = new Map([...container.querySelectorAll(selector)].map((item) => [item.dataset.mediaId, item]));
    for (const id of [...visible, ...hidden]) {
      const item = items.get(id);
      if (!item) continue;
      item.hidden = hidden.includes(id);
      container.append(item);
    }
  }

  #reset() {
    this.track.scrollTo({ left: 0, behavior: 'auto' });
    this.strip?.scrollTo({ left: 0, behavior: 'auto' });
  }

  #schedule() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  }

  go(index) {
    const slide = this.#slides[index];
    if (!slide) return;
    // Rect deltas work the same in LTR and RTL tracks.
    const delta = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
    this.track.scrollBy({ left: delta, behavior: this.#reduceMotion ? 'auto' : 'smooth' });
  }

  #update(force = false) {
    const width = this.track.clientWidth || 1;
    const index = Math.round(Math.abs(this.track.scrollLeft) / width);
    if (index === this.#index && !force) return;
    this.#index = index;
    this.#dots.forEach((dot, i) => dot.toggleAttribute('data-active', i === index));
    if (this.counter) this.counter.textContent = String(index + 1);
    const thumbs = this.#thumbs;
    thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === index ? 'true' : 'false'));
    this.#revealThumb(thumbs[index]);
  }

  #revealThumb(thumb) {
    if (!thumb || !this.strip) return;
    const strip = this.strip.getBoundingClientRect();
    const rect = thumb.getBoundingClientRect();
    const behavior = this.#reduceMotion ? 'auto' : 'smooth';
    if (rect.left < strip.left) this.strip.scrollBy({ left: rect.left - strip.left, behavior });
    else if (rect.right > strip.right) this.strip.scrollBy({ left: rect.right - strip.right, behavior });
  }
}

if (!customElements.get('s-gallery-pager')) customElements.define('s-gallery-pager', SGalleryPager);
